'use client'

import useScreenSize from '@/src/hooks/useScreenSize'
import { cx } from '@/src/styles/stylebook'
import { useState, useEffect, useRef, useCallback } from 'react'

interface SvgTextProps {
  children: any
  className?: string
  strokeWidth1?: number
  strokeWidth2?: number
  strokeColor1?: string
  strokeColor2?: string
  fillColor?: string
  fontSize?: string
}

export default function SvgText({
  children,
  className = '',
  strokeWidth1 = 7.2, //outer
  strokeWidth2 = 3.6, //inner
  strokeColor1 = '#000000',
  strokeColor2 = '#03c75a',
  fillColor = '#000000',
  fontSize = '4rem',
}: SvgTextProps) {
  const [textDimensions, setTextDimensions] = useState({ width: 0, height: 0 })
  const textRef = useRef<SVGTextElement>(null)
  const measureRef = useRef<HTMLSpanElement>(null)
  const { width } = useScreenSize()

  // 텍스트 크기를 측정하는 함수
  const measureTextDimensions = useCallback(() => {
    if (measureRef.current) {
      const rect = measureRef.current.getBoundingClientRect()
      const computedStyle = window.getComputedStyle(measureRef.current)
      const lineHeight = parseFloat(computedStyle.lineHeight) || rect.height

      setTextDimensions({
        width: rect.width,
        height: Math.max(rect.height, lineHeight * 1.2),
      })
    }
  }, [])

  // children 변경 시 텍스트 크기 측정
  useEffect(() => {
    measureTextDimensions()
  }, [children, measureTextDimensions, width])

  // resize 이벤트 리스너 추가 (debounced)
  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    // 초기 마운트 시에도 측정
    measureTextDimensions()

    // cleanup
    return () => {
      clearTimeout(timeoutId)
    }
  }, [measureTextDimensions])

  return (
    <>
      {/* Hidden element for measuring text dimensions */}
      <span
        ref={measureRef}
        style={{
          position: 'absolute',
          visibility: 'hidden',
          fontSize: fontSize,
          fontFamily: 'inherit',
          fontWeight: 'inherit',
          whiteSpace: 'nowrap',
        }}
        aria-hidden="true"
      >
        {children}
      </span>

      {/* SVG with dynamic dimensions */}
      <svg
        viewBox={`0 0 ${textDimensions.width + strokeWidth1 * 4} ${textDimensions.height + strokeWidth1 * 4}`}
        className={cx(className, 'select-none')}
        style={{
          display: 'block',
          width: 'auto',
          height: '100%',
        }}
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <filter id="outline">
            <feMorphology operator="dilate" radius={strokeWidth1} />
            <feFlood floodColor={strokeColor1} />
            <feComposite in="SourceGraphic" operator="over" />
          </filter>
        </defs>

        {/* Base text with fill */}
        <text
          ref={textRef}
          x={strokeWidth1 * 2}
          y={textDimensions.height * 1.1 + strokeWidth1 * 2}
          fontSize={fontSize}
          fill={fillColor}
          dominantBaseline="alphabetic"
          style={{
            paintOrder: 'stroke fill',
            stroke: strokeColor1,
            strokeWidth: strokeWidth1 * 2,
            strokeLinejoin: 'round',
            strokeLinecap: 'round',
          }}
        >
          {children}
        </text>

        {/* Inner stroke overlay */}
        <text
          x={strokeWidth1 * 2}
          y={textDimensions.height * 1.1 + strokeWidth1 * 2}
          fontSize={fontSize}
          fill={fillColor}
          dominantBaseline="alphabetic"
          style={{
            paintOrder: 'stroke fill',
            stroke: strokeColor2,
            strokeWidth: strokeWidth2 * 2,
            strokeLinejoin: 'round',
            strokeLinecap: 'round',
          }}
        >
          {children}
        </text>
      </svg>
    </>
  )
}
